import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { getFeaturedPackages } from '../api/client'
import type { Package } from '../types/packages'

const LANGUAGE_COLORS: Record<string, string> = {
  go: 'bg-cyan-500',
  python: 'bg-yellow-500',
  typescript: 'bg-blue-500',
  javascript: 'bg-yellow-400',
  rust: 'bg-orange-600',
  java: 'bg-red-500',
  ruby: 'bg-red-600',
  swift: 'bg-orange-500',
  kotlin: 'bg-purple-500',
  csharp: 'bg-purple-600',
}

function PackageIcon({ pkg }: { pkg: Package }) {
  const [imgError, setImgError] = useState(false)

  if (pkg.icon_url && !imgError) {
    return (
      <img
        src={pkg.icon_url}
        alt={pkg.name}
        className="w-10 h-10 rounded-lg object-cover"
        onError={() => setImgError(true)}
      />
    )
  }

  const color = LANGUAGE_COLORS[pkg.language?.toLowerCase()] ?? 'bg-gray-500'
  const initials = pkg.slug.slice(0, 2).toUpperCase()

  return (
    <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
      <span className="text-white text-xs font-bold">{initials}</span>
    </div>
  )
}

export default function PackageStrip() {
  const [packages, setPackages] = useState<Package[]>([])
  const stripRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getFeaturedPackages()
      .then(data => setPackages(data ?? []))
      .catch(() => {/* silently skip if unavailable */})
  }, [])

  if (packages.length === 0) return null

  return (
    <section className="py-12 px-4 border-t border-gray-100 dark:border-gray-800">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Built with Factory
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Real tools shipped by Factory agents — download and use them today
            </p>
          </div>
          <Link
            to="/packages"
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1 transition-colors"
          >
            Browse all
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>

        {/* Horizontal scrolling strip */}
        <div
          ref={stripRef}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {packages.map((pkg) => (
            <Link
              key={pkg.id}
              to={`/packages/${pkg.slug}`}
              className="flex-shrink-0 flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-sm transition-all group min-w-[160px] max-w-[200px]"
            >
              <PackageIcon pkg={pkg} />
              <div className="overflow-hidden min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {pkg.name}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                  {pkg.total_downloads > 0
                    ? `${pkg.total_downloads >= 1000 ? `${(pkg.total_downloads / 1000).toFixed(1)}k` : pkg.total_downloads} downloads`
                    : pkg.latest_version ?? pkg.language}
                </p>
              </div>
              {pkg.is_official && (
                <span className="ml-auto flex-shrink-0 text-primary-500 dark:text-primary-400" title="Official">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </Link>
          ))}

          {/* Browse all card */}
          <Link
            to="/packages"
            className="flex-shrink-0 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl px-5 py-3 hover:border-primary-400 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 text-sm text-gray-500 dark:text-gray-400 transition-all min-w-[120px]"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            View all
          </Link>
        </div>
      </div>
    </section>
  )
}
